import type { ReactElement } from 'react';
import { Modal } from '@mantine/core';
import classes from './Footer.module.css';
import { X } from 'react-feather';
import { useTheme } from '@/assets/styles/themeMantine';
import { useSmallScreen } from '@/hooks/useWindowDimension';

interface OverstoryComponentProps {
  classes: CSSModuleClasses;
}

interface DisclaimerComponentProps {
  onClickEvent: () => void;
}

interface TermsComponentProps {
  useFullVersion: boolean;
  classes: CSSModuleClasses;
}

interface PrivacyComponentProps {
  useFullVersion: boolean;
  classes: CSSModuleClasses;
}

export const OverstoryComponent = ({ classes }: OverstoryComponentProps) => {
  return <span id={classes.overstory}>Overstory</span>;
};

export const DisclaimerComponent = ({
  onClickEvent,
}: DisclaimerComponentProps) => {
  return (
    <span className={classes.disclaimerLink} onClick={onClickEvent}>
      Disclaimer
    </span>
  );
};

export const TermsComponent = ({
  useFullVersion,
  classes,
}: TermsComponentProps) => {
  return (
    <a
      className={classes.link}
      href="https://www.overstory.com/terms-of-service"
      target="_blank"
      rel="noreferrer"
    >
      {useFullVersion ? 'Terms and Conditions' : 'Terms'}
    </a>
  );
};

export const PrivacyComponent = ({
  useFullVersion,
  classes,
}: PrivacyComponentProps) => {
  return (
    <a
      className={classes.link}
      href="https://www.overstory.com/product-privacy-statement"
      target="_blank"
      rel="noreferrer"
    >
      {useFullVersion ? 'Privacy Policy' : 'Privacy'}
    </a>
  );
};

export const DEFAULT_DISCLAIMER_COPY =
  'While Overstory strives to provide an accurate view of the state of vegetation and the risks, there may be inaccuracies in the information, as well as unpredictability in nature and human behavior. The information in this document is one source among others to consider when making decisions about your vegetation management.';

interface DisclaimerModalProps {
  title?: string;
  body?: string | ReactElement;
  useFullVersion: boolean;
  opened: boolean;
  onCloseEvent: () => void;
}

export const DisclaimerModal = ({
  title,
  body,
  useFullVersion,
  opened,
  onCloseEvent,
}: DisclaimerModalProps) => {
  const isSmallScreen = useSmallScreen();
  const theme = useTheme();

  const titleElement = (
    <>
      <span>{title}</span>
      <span className={classes.closeButton}>
        <X size={24} onClick={onCloseEvent}></X>
      </span>
    </>
  );
  return (
    <Modal
      opened={opened}
      withCloseButton={false}
      onClose={onCloseEvent}
      centered
      size={isSmallScreen ? '100%' : '500px'}
      fullScreen={useFullVersion}
      title={titleElement}
      classNames={{
        inner: classes.inner,
        title: classes.title,
        content: useFullVersion ? classes.content : classes.resetHeightContent,
      }}
      styles={{
        title: {
          fontSize: theme.other.typography.fontSize,
          fontWeight: theme.other.typography.weight.medium,
        },
        body: {
          fontSize: theme.other.typography.size.sm,
          fontWeight:
            theme.other.typography.variants.bodySmallRegular.fontWeight,
          lineHeight: theme.other.typography.lineHeight.md,
          paddingTop: '8px',
        },
      }}
    >
      {body ?? DEFAULT_DISCLAIMER_COPY}
    </Modal>
  );
};
